import { IFluidObject } from '@fluidframework/core-interfaces';
import { MessageBannerProvider } from '@fluidx/office-fluid-types';
import { EventEmitter } from 'events';
import { PostMessageSender, PostMessageHandlers } from '../Shared/PostMessage';
import {
  CreateBannerMessageType,
  RemoveBannerMessageType,
  BannerListenerAttachedMessageType,
  CreateBannerMessage,
  RemoveBannerMessage
} from '../MessageTypes/MessageBannerProviderMessageTypes';

class HostSideMessageBannerProviderProxy extends EventEmitter implements MessageBannerProvider {
  get MessageBannerProvider() {
    return this;
  }
}

/**
 * Initializes everything needed to handle the MessageBannerProvider interface outside the iframe.
 * @returns A dummy component with just this interface and the set of message handlers this interface is expecting
 */
export function getHostSideMessageBannerProvider(
  sender: PostMessageSender
): {
  component: MessageBannerProvider;
  handlers: PostMessageHandlers<string>;
  dispose: () => void;
} {
  const messageBannerProvider = new HostSideMessageBannerProviderProxy();

  const handlers: PostMessageHandlers<string> = {
    [CreateBannerMessageType]: (message) => {
      const { bannerInfo } = (message as CreateBannerMessage).payload;
      messageBannerProvider.emit('CreateBanner', bannerInfo);
    },
    [RemoveBannerMessageType]: (message) => {
      const { bannerId } = (message as RemoveBannerMessage).payload;
      messageBannerProvider.emit('RemoveBanner', bannerId);
    }
  };

  messageBannerProvider.on('BannerListenerAttached', () => {
    sender.send({ type: BannerListenerAttachedMessageType });
  });

  return {
    component: messageBannerProvider,
    handlers,
    dispose: () => {
      messageBannerProvider.removeAllListeners();
    }
  };
}

/**
 * Initializes everything needed to handle the MessageBannerProvider interface inside the iframe.
 * @param component the actual component with the real implementation of the interface
 * @param sender how to send messages to the outside of the iframe
 */
export function getGuestSideMessageBannerProvider(
  component: IFluidObject,
  sender: PostMessageSender
): PostMessageHandlers<string> | undefined {
  const messageBannerProvider = component.MessageBannerProvider;
  if (!messageBannerProvider) {
    return undefined;
  }

  messageBannerProvider.on('CreateBanner', (bannerInfo) => {
    sender.send({ type: CreateBannerMessageType, payload: { bannerInfo } });
  });

  messageBannerProvider.on('RemoveBanner', (bannerId) => {
    sender.send({ type: RemoveBannerMessageType, payload: { bannerId } });
  });

  return {
    [BannerListenerAttachedMessageType]: () => messageBannerProvider.emit('BannerListenerAttached')
  };
}
